import { deepClone } from './clone';
import type { GameEvent, GameState, PlayerId, PlayerView } from './types';

/**
 * O que `me` pode ver. Esconde seed, baralhos, missões e cartas dos outros.
 * `me = null` gera a visão de um espectador.
 */
export function getPlayerView(s: GameState, me: PlayerId | null): PlayerView {
  const over = s.phase === 'over';
  const players = s.players.map((p) => {
    const mine = p.id === me;
    return {
      id: p.id,
      name: p.name,
      color: p.color,
      vocation: p.vocation,
      kind: p.kind,
      difficulty: p.difficulty,
      alive: p.alive,
      eliminatedBy: p.eliminatedBy,
      bossKills: p.bossKills,
      harmony: p.harmony,
      cardCount: p.cards.length,
      lootCount: p.loot.length,
      ...(mine ? { cards: [...p.cards], loot: [...p.loot] } : {}),
      ...(mine || over ? { mission: deepClone(p.mission) } : {}),
    };
  });
  let pending: PlayerView['pending'] = s.pending ? deepClone(s.pending) : null;
  if (pending?.kind === 'lootChoice' && pending.player !== me) pending = { ...pending, options: [] };
  // A janela do amuleto é secreta: só quem tem a carta sabe que está decidindo.
  if (pending?.kind === 'reaction' && pending.player !== me) pending = { kind: 'hidden', player: -1 };
  return {
    me,
    info: s.info,
    phase: s.phase,
    round: s.round,
    current: s.current,
    setup: s.setup ? deepClone(s.setup) : null,
    reinforcements: s.reinforcements,
    territories: deepClone(s.territories),
    players,
    deckCount: s.deck.length,
    tradeCount: s.tradeCount,
    boss: s.boss ? { ...s.boss } : null,
    effects: deepClone(s.effects),
    attacked: deepClone(s.attacked),
    regionFirst: { ...s.regionFirst },
    bounty: s.bounty,
    skulls: [...s.skulls],
    defenseUsed: [...s.defenseUsed],
    turn: deepClone(s.turn),
    pending,
    eliminationOrder: [...s.eliminationOrder],
    result: s.result ? deepClone(s.result) : null,
    seq: s.seq,
    events: filterEvents(s.events, me),
  };
}

export function filterEvents(events: GameEvent[], me: PlayerId | null): GameEvent[] {
  const out: GameEvent[] = [];
  for (const e of events) {
    switch (e.type) {
      case 'cardDrawn':
        out.push(e.player === me ? e : { ...e, card: null });
        break;
      case 'lootDrawn':
        out.push(e.player === me ? e : { ...e, options: [] });
        break;
      case 'lootChosen':
        out.push(e.player === me ? e : { ...e, kind: null });
        break;
      case 'missionChanged':
        if (e.player === me) out.push(e);
        break;
      case 'regionFirst':
        out.push(e.player === me ? e : { ...e, loot: null });
        break;
      case 'combatPreview':
        if (e.reactor === me) out.push(e);
        break;
      case 'lootPlayed':
        out.push(e.cards && e.player !== me && e.victim !== me ? { ...e, cards: undefined } : e);
        break;
      default:
        out.push(e);
    }
  }
  return out;
}
